import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Table,
  Badge,
  Spinner,
  Alert,
  Form,
  InputGroup,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import api from "../../services/api";
import "./Dashboard.css";

const PESERTA_PAGE_SIZE = 20;
const PESERTA_SEARCH_MIN = 2;

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const _FILTER_KEY = "_laporan_kinerja_state";

const LaporanKinerjaPendamping = () => {
  const navigate = useNavigate();

  // Restore saved filter state when returning from detail page
  const _saved = (() => {
    try {
      const raw = sessionStorage.getItem(_FILTER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  // Dropdown state (server-side search, tidak load semua data sekaligus)
  const [pesertaList, setPesertaList] = useState([]);
  const [loadingPeserta, setLoadingPeserta] = useState(false);
  const [pesertaError, setPesertaError] = useState(null);
  const [pesertaTotal, setPesertaTotal] = useState(0);
  const searchRequestRef = useRef(0);

  // Combobox search
  const [searchText, setSearchText] = useState(_saved?.searchText || "");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedPendamping, setSelectedPendamping] = useState(
    _saved?.selectedPendamping || null,
  );
  const comboRef = useRef(null);

  // Kinerja table state
  const [kinerjaData, setKinerjaData] = useState(_saved?.kinerjaData || []);
  const [loadingKinerja, setLoadingKinerja] = useState(false);
  const [kinerjaError, setKinerjaError] = useState(null);
  const [totalKinerja, setTotalKinerja] = useState(_saved?.totalKinerja || 0);

  // Rekap state
  const [rekapData, setRekapData] = useState(_saved?.rekapData || null);

  // Date filter — default: Jan 1 of current year → today
  const today = new Date();
  const defaultDari = `${today.getFullYear()}-01-01`;
  const defaultSampai = today.toISOString().slice(0, 10);
  const [tglDari, setTglDari] = useState(_saved?.tglDari || defaultDari);
  const [tglSampai, setTglSampai] = useState(
    _saved?.tglSampai || defaultSampai,
  );

  // Table search
  const [tableSearch, setTableSearch] = useState(_saved?.tableSearch || "");

  // Clear saved filter state once restored
  useEffect(() => {
    sessionStorage.removeItem(_FILTER_KEY);
  }, []);

  const fetchPesertaOptions = async (query = "") => {
    const requestId = ++searchRequestRef.current;
    try {
      setLoadingPeserta(true);
      setPesertaError(null);
      const params = {
        page: 1,
        page_size: PESERTA_PAGE_SIZE,
        status: "DISETUJUI",
      };
      if (query) params.q = query;
      const res = await api.get("/pendamping/laporan-peserta", { params });
      if (requestId !== searchRequestRef.current) return;
      if (res.data.success) {
        setPesertaList(res.data.data || []);
        setPesertaTotal(
          res.data.pagination?.total ?? res.data.total ?? res.data.data?.length ?? 0,
        );
      } else {
        setPesertaError("Gagal memuat data pendamping");
        setPesertaList([]);
        setPesertaTotal(0);
      }
    } catch (err) {
      if (requestId !== searchRequestRef.current) return;
      setPesertaError(
        "Gagal memuat data pendamping: " +
          (err.response?.data?.message || err.message),
      );
      setPesertaList([]);
      setPesertaTotal(0);
    } finally {
      if (requestId === searchRequestRef.current) {
        setLoadingPeserta(false);
      }
    }
  };

  // Pencarian server-side saat user mengetik
  useEffect(() => {
    const trimmed = searchText.trim();
    if (trimmed.length > 0 && trimmed.length < PESERTA_SEARCH_MIN) {
      setPesertaList([]);
      setPesertaTotal(0);
      return;
    }
    const timer = setTimeout(() => {
      if (trimmed.length === 0) return;
      fetchPesertaOptions(trimmed);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (comboRef.current && !comboRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleComboFocus = () => {
    setDropdownOpen(true);
    const trimmed = searchText.trim();
    if (trimmed.length === 0 && pesertaList.length === 0 && !loadingPeserta) {
      fetchPesertaOptions("");
    }
  };

  const handleSelectPendamping = (p) => {
    setSelectedPendamping(p);
    setSearchText(p.nama || "");
    setDropdownOpen(false);
    setTableSearch("");
    setKinerjaData([]);
    setTotalKinerja(0);
    setKinerjaError(null);
    setRekapData(null);
  };

  const handleTampilkan = () => {
    if (!selectedPendamping) return;
    if (!tglDari || !tglSampai) {
      setKinerjaError("Pilih periode tanggal terlebih dahulu");
      return;
    }
    if (tglDari > tglSampai) {
      setKinerjaError(
        "Tanggal dari tidak boleh lebih besar dari tanggal sampai",
      );
      return;
    }
    setKinerjaError(null);
    const id = selectedPendamping.id_pendamping;
    fetchKinerja(id, tglDari, tglSampai);
    fetchRekap(id, tglDari, tglSampai);
  };

  const fetchKinerja = async (idPendamping, dari, sampai) => {
    try {
      setLoadingKinerja(true);
      setKinerjaData([]);
      const res = await api.get(
        `/pendamping/laporan-kinerja/${idPendamping}`,
        {
          params: { tgl_dari: dari, tgl_sampai: sampai },
        },
      );
      if (res.data.success) {
        setKinerjaData(res.data.data || []);
        setTotalKinerja(res.data.total || 0);
        setKinerjaError(null);
      } else {
        setKinerjaError("__not_found__");
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setKinerjaError(msg || "Terjadi kesalahan");
    } finally {
      setLoadingKinerja(false);
    }
  };

  const fetchRekap = async (idPendamping, dari, sampai) => {
    try {
      const res = await api.get(
        `/pendamping/rekap-kinerja/${idPendamping}`,
        {
          params: { tgl_dari: dari, tgl_sampai: sampai },
        },
      );
      if (res.data.success) setRekapData(res.data.data || null);
    } catch (err) {
      console.error("Gagal memuat rekap:", err.message);
    }
  };

  const filteredKinerja = kinerjaData.filter((row) => {
    if (!tableSearch) return true;
    const q = tableSearch.toLowerCase();
    return (
      (row.no_daftar || "").toLowerCase().includes(q) ||
      (row.nama_pu || "").toLowerCase().includes(q) ||
      (row.merek_dagang || "").toLowerCase().includes(q) ||
      (row.prov_pu || "").toLowerCase().includes(q) ||
      (row.kota_pu || "").toLowerCase().includes(q) ||
      (row.status_desc || "").toLowerCase().includes(q) ||
      (row.jenis_produk_desc || "").toLowerCase().includes(q)
    );
  });

  const getStatusBadge = (status) => {
    if (!status) return <Badge bg="secondary">-</Badge>;
    const s = status.toLowerCase();
    if (s.includes("terbit") || s.includes("selesai"))
      return <Badge bg="success">{status}</Badge>;
    if (s.includes("proses") || s.includes("verif"))
      return <Badge bg="primary">{status}</Badge>;
    if (s.includes("tolak") || s.includes("batal"))
      return <Badge bg="danger">{status}</Badge>;
    if (s.includes("kirim") || s.includes("dokumen"))
      return (
        <Badge bg="warning" text="dark">
          {status}
        </Badge>
      );
    return <Badge bg="info">{status}</Badge>;
  };

  return (
    <div>
      {/* Page header */}
      <div
        className="page-header-banner"
        style={{
          background:
            "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 50%, var(--primary-dark) 100%)",
        }}
      >
        <div className="page-header-content">
          <div className="page-header-left">
            <div className="page-header-icon">
              <i className="fas fa-chart-line"></i>
            </div>
            <div>
              <h1 className="page-header-title">Laporan Kinerja Pendamping</h1>
              <p className="page-header-subtitle">
                Monitoring dan evaluasi kinerja pendamping
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Pendamping selector */}
      <Card className="mb-3" style={{ overflow: "visible" }}>
        <Card.Body style={{ overflow: "visible" }}>
          <Form.Label className="fw-semibold mb-1">
            <i className="fas fa-user-tie me-2"></i>Pilih Pendamping
          </Form.Label>

          {pesertaError && (
            <Alert variant="danger" className="py-2 mb-2">
              <i className="fas fa-exclamation-circle me-2"></i>
              {pesertaError}
            </Alert>
          )}

          <div ref={comboRef} style={{ position: "relative", maxWidth: 480 }}>
            <InputGroup>
              <InputGroup.Text>
                {loadingPeserta ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <i className="fas fa-search"></i>
                )}
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={
                  loadingPeserta
                    ? "Mencari pendamping..."
                    : `Ketik min. ${PESERTA_SEARCH_MIN} karakter (nama, email, no HP, NIK)...`
                }
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setDropdownOpen(true);
                  if (!e.target.value) {
                    setSelectedPendamping(null);
                    setKinerjaData([]);
                    setPesertaList([]);
                    setPesertaTotal(0);
                  }
                }}
                onFocus={handleComboFocus}
                autoComplete="off"
              />
              {searchText && (
                <InputGroup.Text
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setSearchText("");
                    setSelectedPendamping(null);
                    setKinerjaData([]);
                    setPesertaList([]);
                    setPesertaTotal(0);
                    setDropdownOpen(false);
                  }}
                >
                  <i className="fas fa-times text-muted"></i>
                </InputGroup.Text>
              )}
            </InputGroup>

            {/* Dropdown list */}
            {dropdownOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 1050,
                  background: "#fff",
                  border: "1px solid #dee2e6",
                  borderTop: "none",
                  borderRadius: "0 0 4px 4px",
                  maxHeight: 280,
                  overflowY: "auto",
                  boxShadow: "0 4px 12px rgba(0,0,0,.1)",
                }}
              >
                {loadingPeserta ? (
                  <div className="px-3 py-3 text-muted small text-center">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Mencari...
                  </div>
                ) : searchText.trim().length > 0 &&
                  searchText.trim().length < PESERTA_SEARCH_MIN ? (
                  <div className="px-3 py-2 text-muted small">
                    Ketik minimal {PESERTA_SEARCH_MIN} karakter untuk mencari
                  </div>
                ) : pesertaList.length === 0 ? (
                  <div className="px-3 py-2 text-muted small">
                    {searchText.trim()
                      ? "Tidak ada pendamping ditemukan"
                      : "Ketik nama, email, no HP, atau NIK pendamping"}
                  </div>
                ) : (
                  pesertaList.map((p, idx) => (
                    <div
                      key={p.id_pendamping || idx}
                      className="px-3 py-2"
                      style={{
                        cursor: "pointer",
                        borderBottom: "1px solid #f0f0f0",
                        background:
                          selectedPendamping?.id_pendamping === p.id_pendamping
                            ? "#e8f4fd"
                            : "transparent",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#f5f5f5")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background =
                          selectedPendamping?.id_pendamping === p.id_pendamping
                            ? "#e8f4fd"
                            : "transparent")
                      }
                      onClick={() => handleSelectPendamping(p)}
                    >
                      <div className="fw-semibold small">{p.nama || "-"}</div>
                      <div
                        className="text-muted"
                        style={{ fontSize: "0.75rem" }}
                      >
                        {p.email || ""}
                        {p.no_hp ? ` · ${p.no_hp}` : ""}
                      </div>
                    </div>
                  ))
                )}
                {!loadingPeserta && pesertaList.length > 0 && (
                  <div
                    className="px-3 py-2 text-muted border-top"
                    style={{ fontSize: "0.72rem" }}
                  >
                    Menampilkan {pesertaList.length}
                    {pesertaTotal > pesertaList.length
                      ? ` dari ${pesertaTotal.toLocaleString("id-ID")}`
                      : ""}
                    {searchText.trim()
                      ? " — persempit kata kunci jika belum ketemu"
                      : " — ketik untuk mencari pendamping lain"}
                  </div>
                )}
              </div>
            )}
          </div>

          {selectedPendamping && (
            <div
              className="mt-2 d-flex gap-3 flex-wrap"
              style={{ fontSize: "0.85rem" }}
            >
              <span className="text-muted">
                <i className="fas fa-envelope me-1"></i>
                {selectedPendamping.email || "-"}
              </span>
              <span className="text-muted">
                <i className="fas fa-phone me-1"></i>
                {selectedPendamping.no_hp || "-"}
              </span>
            </div>
          )}

          {/* Date range filter */}
          {selectedPendamping && (
            <div className="mt-3 d-flex align-items-end gap-2 flex-wrap">
              <div>
                <Form.Label className="small mb-1 fw-semibold">
                  Tanggal Dari
                </Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={tglDari}
                  onChange={(e) => setTglDari(e.target.value)}
                  style={{ width: 160 }}
                />
              </div>
              <div>
                <Form.Label className="small mb-1 fw-semibold">
                  Tanggal Sampai
                </Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={tglSampai}
                  onChange={(e) => setTglSampai(e.target.value)}
                  style={{ width: 160 }}
                />
              </div>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleTampilkan}
                disabled={loadingKinerja || !tglDari || !tglSampai}
              >
                {loadingKinerja ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Memuat...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search me-1"></i>Tampilkan
                  </>
                )}
              </button>
              {totalKinerja > 0 && (
                <Badge bg="primary" className="align-self-end mb-1">
                  Total: {totalKinerja} pengajuan
                </Badge>
              )}
            </div>
          )}

          {/* Rekap summary */}
          {rekapData && (
            <div
              className="mt-3 p-3 rounded"
              style={{ background: "#f8f9fa", border: "1px solid #e9ecef" }}
            >
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className="fas fa-chart-bar text-primary"></i>
                <span className="fw-semibold" style={{ fontSize: "0.9rem" }}>
                  Rekapitulasi Pengajuan
                </span>
                <span className="text-muted small">
                  — {rekapData.namaprovinsi}, {rekapData.namakabupaten}
                </span>
              </div>
              <div className="d-flex flex-wrap gap-2">
                {[
                  { label: "Total", value: rekapData.total, bg: "primary" },
                  {
                    label: "Terbit SH",
                    value: rekapData.terbitsh,
                    bg: "success",
                  },
                  {
                    label: "Proses P3H",
                    value: rekapData.prosesp3h,
                    bg: "info",
                  },
                  {
                    label: "Dikirim ke KF",
                    value: rekapData.dikirimkekf,
                    bg: "info",
                  },
                  {
                    label: "Penerbitan Sertifikat",
                    value: rekapData.penerbitansertifikat,
                    bg: "info",
                  },
                  {
                    label: "Dikembalikan",
                    value: rekapData.dikembalikan,
                    bg: "warning",
                  },
                  {
                    label: "Dikembalikan KF",
                    value: rekapData.dikembalikankf,
                    bg: "warning",
                  },
                  {
                    label: "Dibatalkan",
                    value: rekapData.dibatalkan,
                    bg: "danger",
                  },
                  { label: "Ditolak", value: rekapData.ditolak, bg: "danger" },
                  {
                    label: "Submitted",
                    value: rekapData.submited,
                    bg: "secondary",
                  },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="text-center px-3 py-2 rounded"
                    style={{
                      background: "#fff",
                      border: "1px solid #dee2e6",
                      minWidth: 100,
                    }}
                  >
                    <div
                      className="fw-bold"
                      style={{
                        fontSize: "1.2rem",
                        color: `var(--bs-${item.bg})`,
                      }}
                    >
                      {item.value ?? 0}
                    </div>
                    <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Kinerja table */}
      {selectedPendamping && (
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span className="fw-semibold">
              <i className="fas fa-list me-2"></i>
              Daftar Pengajuan — {selectedPendamping.nama}
            </span>
            <Form.Control
              type="text"
              size="sm"
              placeholder="Cari no. daftar, nama PU, merek..."
              style={{ maxWidth: 280 }}
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
            />
          </Card.Header>
          <Card.Body className="p-0">
            {kinerjaError && kinerjaError !== "__not_found__" && (
              <Alert variant="danger" className="m-3">
                <i className="fas fa-exclamation-circle me-2"></i>
                {kinerjaError}
              </Alert>
            )}
            {loadingKinerja ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Memuat data pengajuan...</p>
              </div>
            ) : kinerjaError === "__not_found__" ||
              (!loadingKinerja &&
                kinerjaData.length === 0 &&
                (kinerjaError || totalKinerja === 0) &&
                filteredKinerja.length === 0) ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-inbox fa-2x mb-2 d-block"></i>
                <p className="mb-0">
                  Data tidak ditemukan untuk periode yang dipilih
                </p>
              </div>
            ) : filteredKinerja.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-search fa-2x mb-2 d-block"></i>
                <p className="mb-0">
                  Tidak ada data yang cocok dengan pencarian
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="mb-0" style={{ fontSize: "0.85rem" }}>
                  <thead className="table-light">
                    <tr>
                      <th className="px-3">#</th>
                      <th>No. Daftar</th>
                      <th>Tgl. Daftar</th>
                      <th>Nama PU</th>
                      <th>Merek Dagang</th>
                      <th>Provinsi</th>
                      <th>Kota/Kab</th>
                      <th>Jenis Produk</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredKinerja.map((row, idx) => (
                      <tr key={row.id_reg || idx}>
                        <td className="px-3 text-muted">{idx + 1}</td>
                        <td className="fw-semibold text-nowrap">
                          {row.no_daftar || "-"}
                        </td>
                        <td className="text-nowrap">
                          {formatDate(row.tgl_daftar)}
                        </td>
                        <td>{row.nama_pu || "-"}</td>
                        <td>{row.merek_dagang || "-"}</td>
                        <td>{row.prov_pu || "-"}</td>
                        <td>{row.kota_pu || "-"}</td>
                        <td style={{ maxWidth: 220 }}>
                          {row.jenis_produk_desc || "-"}
                        </td>
                        <td className="text-nowrap">
                          {getStatusBadge(row.status_desc)}
                        </td>
                        <td className="text-nowrap">
                          {row.id_reg && (
                            <button
                              className="btn btn-outline-primary btn-sm py-0 px-2"
                              style={{ fontSize: "0.75rem" }}
                              onClick={() => {
                                sessionStorage.setItem(
                                  _FILTER_KEY,
                                  JSON.stringify({
                                    selectedPendamping,
                                    searchText,
                                    tglDari,
                                    tglSampai,
                                    kinerjaData,
                                    rekapData,
                                    totalKinerja,
                                    tableSearch,
                                  }),
                                );
                                const token = Math.random()
                                  .toString(36)
                                  .slice(2, 13);
                                localStorage.setItem(
                                  `_dreg_${token}`,
                                  row.id_reg,
                                );
                                navigate(
                                  `/admin/pendamping/detail-registrasi/${token}`,
                                );
                              }}
                            >
                              <i className="fas fa-external-link-alt me-1"></i>
                              Detil
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
            {filteredKinerja.length > 0 && (
              <div className="px-3 py-2 text-muted small border-top">
                Menampilkan {filteredKinerja.length} dari {kinerjaData.length}{" "}
                data
              </div>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default LaporanKinerjaPendamping;
